/* eslint-disable react/prop-types */
import React, { useMemo, useState } from 'react';
import { Table, Typography, Modal } from 'antd';
import { Button as CrceButton } from '@crc-evans/ui-components';
import moment from 'moment';
import InspectionLogTableCell from './InspectionLogTableCell';
import { getProjectTableLoadingConfig } from './ProjectTableLoader';
import {
  REJECT_CELL_COLOR,
  findTestDefinitionForColumn,
  formatValueDisplayString,
  getColumnTitleText,
  getTableColumnsForModal,
  getTableDataForModal,
  getTableRowRangeStatus,
  isTableMultipleComparatorMode,
  isTableJsonValue,
  parseJsonCellValue,
  parseTestTableData,
} from '../utils/inspectionLogCell';

const { Text } = Typography;

const LogDetailsTable = (props) => {
  const {
    dataList,
    columnList,
    showLogLoader,
    tableDated,
    onFetchLogDetails,
    totalCount,
    stationTests,
    testTypesList,
  } = props;

  const [tableResultItem, setTableResultItem] = useState(null);

  const enhancedColumns = useMemo(() => {
    if (!columnList?.length) return [];

    return columnList.map((col) => {
      const titleText = getColumnTitleText(col);
      const testDef = findTestDefinitionForColumn(
        { ...col, title: titleText, columnTitle: col.columnTitle ?? titleText },
        stationTests
      );

      return {
        ...col,
        ellipsis: { showTitle: false },
        render: (text) => (
          <InspectionLogTableCell
            text={text}
            column={col}
            testDef={testDef}
            testTypesList={testTypesList}
            onOpenTable={setTableResultItem}
          />
        ),
      };
    });
  }, [columnList, stationTests, testTypesList]);

  const tableModalColumns = useMemo(() => {
    const columns = getTableColumnsForModal(tableResultItem);
    if (!tableResultItem?.value) return columns;

    const parsedValueData = parseJsonCellValue(tableResultItem.value);
    if (!parsedValueData?.rows) return columns;

    return columns.map((col) => {
      if (col.dataIndex === 'name' && isTableMultipleComparatorMode(tableResultItem)) {
        return {
          ...col,
          render: (text, record) => {
            const rowIndex = record.key;
            const valueRow = parsedValueData?.rows?.[rowIndex];
            const templateRow = parseTestTableData(tableResultItem)?.rows?.[rowIndex];
            const rowStatus = getTableRowRangeStatus(valueRow, templateRow, tableResultItem);

            if (rowStatus === false) {
              return <span style={{ color: REJECT_CELL_COLOR }}>{text}</span>;
            }

            return text;
          },
        };
      }

      return col;
    });
  }, [tableResultItem]);

  const tableModalData = useMemo(
    () => getTableDataForModal(tableResultItem),
    [tableResultItem]
  );

  return (
    <div>
      {dataList && dataList.length > 0 ? (
        <>
          <Text className="log-details-dated dated">
            Dated: {moment(tableDated.fromDate).format('DD MMM YYYY')}
          </Text>
          <div className="log-table-shell">
            <Table
              className="log-details-dashboard-table"
              columns={enhancedColumns}
              dataSource={dataList}
              loading={getProjectTableLoadingConfig(showLogLoader)}
              pagination={{
                className: 'heat-profile-pagination',
                position: ['bottomCenter'],
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                showTotal: (total, range) => `${range[0]}–${range[1]} of ${total} items`,
                total: totalCount,
                defaultPageSize: 20,
                onChange: async (page, size) => {
                  const obj = {
                    fromDate: tableDated.fromDate,
                    toDate: tableDated.toDate,
                    sowId: tableDated.sowId,
                    pageSize: size,
                    pageIndex: page - 1,
                  };
                  sessionStorage.setItem(
                    'page',
                    JSON.stringify({
                      pageSize: size,
                      pageIndex: page - 1,
                    })
                  );
                  await onFetchLogDetails(obj);
                },
              }}
              scroll={{ x: '100%', y: '100%' }}
            />
          </div>
        </>
      ) : (
        <div className="log-table-shell">
          <Table className="log-details-dashboard-table" loading={getProjectTableLoadingConfig(showLogLoader)} />
        </div>
      )}

      <Modal
        width="min(92vw, 720px)"
        wrapClassName="log-table-results-modal-wrap"
        className="log-table-results-modal"
        title="Table Results"
        centered
        zIndex={10020}
        getContainer={() => document.body}
        visible={Boolean(tableResultItem)}
        onCancel={() => setTableResultItem(null)}
        maskStyle={{ background: 'rgba(0, 0, 0, 0.45)' }}
        footer={[
          <CrceButton
            key="close"
            label="Close"
            variant="tertiary"
            size="medium"
            onClick={() => setTableResultItem(null)}
          />,
        ]}
      >
        <Table
          columns={tableModalColumns}
          dataSource={tableModalData}
          pagination={false}
          scroll={{ x: 'max-content' }}
          className="log-table-results-inner-table"
        />
      </Modal>
    </div>
  );
};

export default LogDetailsTable;
